import React from 'react'
import { Redirect, Route } from 'react-router-dom'
import { getAuthForPath } from '../util/authSession';
import { PERFIS_AEREO, PERFIS_EVENTOS, PERFIS_RSVP } from '../util/malhaAereo';

// A tela do cargo para o mesmo registro. O link de e-mail que vai a mais de um cargo abre a tela de um deles; quem é
// do outro cai na dele, em vez de numa tela só de leitura, recusada ou de volta ao painel. O admin abre todas.
// `para(id, busca)`: o id que veio no caminho e a query do link.
const TELA_DO_CARGO = [
    // A emissão do RSVP (Solicitar Emissão de Aéreo) e a do Aéreo (Emissão de Aéreo), do mesmo evento.
    { de: '/cadastroEventoEmissaoEnvioAereo/', perfis: PERFIS_AEREO, para: (id) => '/cadastroEventoEmissaoAereo/' + id },
    { de: '/cadastroEventoEmissaoAereo/', perfis: PERFIS_RSVP, para: (id) => '/cadastroEventoEmissaoEnvioAereo/' + id },
    // A Importação das Opções é do RSVP. O Aéreo e Eventos vão à malha do link (`?malha=`, o e-mail de malha liberada)
    // e, sem ela, às solicitações do evento.
    {
        de: '/cadastroEventoPortacaoAereo/', perfis: [...PERFIS_AEREO, ...PERFIS_EVENTOS],
        para: (id, busca) => (busca.get('malha')
            ? '/cadastroEventoImportarMalhaAereo/' + busca.get('malha')
            : '/cadastroEventoSolicitacoesAereo/' + id),
    },
];
const telaDoCargo = (perfil, location) => {
    const regra = TELA_DO_CARGO.find(r => r.perfis.includes(perfil) && location.pathname.startsWith(r.de));
    return regra ? regra.para(location.pathname.slice(regra.de.length), new URLSearchParams(location.search)) : null;
};

// Aéreo: só o Painel de Eventos, Solicitar Malha Aérea (readonly), Importação Aéreo
// e a própria troca de senha. O Gerente Aéreo tem as mesmas telas.
const rotasDoAereo = [
    '/evento',
    '/cadastroEventoSolicitacoesAereo',
    '/cadastroEventoMalhasAereo',
    '/cadastroEventoEmissaoEnvioAereo',
    '/cadastroEventoEmissaoAereo',
    '/cadastroEventoSolicitacaoAereo',
    '/cadastroEventoImportarMalhaAereo',
    '/cadastroEventoSugestaoAereo',
    '/aereoFaturaCvc',
    '/aereo',
    '/rascunhoMalhaAereo',
    '/alterarSenha',
];
const restrictedRoutesByPerfil = {
    suplier: [
        '/fornecedor',
        '/cliente*',
        '/cadastroCliente*',
        '/cadastroFornecedor*',
        // Os atalhos do Painel de Fornecedores. Disparo e revisão não entram aqui:
        // as duas rotas têm `roles` próprio no routes.js, que já inclui o `suplier`.
        '/fornecedorGrupo',
        '/cadastroFornecedorGrupo*',
        '/hotel',
        '/cadastroHotel*',
        '/freelancer',
        '/cadastroFreelancer*',
        '/compra',
        '/cadastroCompra*',
        '/evento*',
        '/cadastroEvento*',
        '/checklist*',
        '/local*',
        '/onePageEvento*',
        '/orcamento*',
        '/cadastroOrcamento*',
        '/importarOrcamento*',
        '/resumoOrcamento*',
        '/cadastroPagamentoOrcamento*',
        '/relatorioPagamentoOrcamento*',
        '/solicitarGasto*',
    ],
    backofficeinterno: ['/evento', '/freelancer', '/cadastroFreelancer*'],
    criacao: ['/evento', '/eventoHistorico*', '/onePageEvento*'],
    aereo: rotasDoAereo,
    gerenteaereo: rotasDoAereo,
};
const fallbackRouteByPerfil = {
    suplier: '/evento',
    backofficeinterno: '/evento',
    criacao: '/evento',
    // O Aéreo entra pela tela Aéreo, o painel dele.
    aereo: '/aereo',
    gerenteaereo: '/aereo',
};
// operadorrsvp/listarsvp não importam planilhas (apenas exportam) — bloqueia o acesso por URL às telas de importação.
const blockedRoutesByPerfil = {
    operadorrsvp: ['/eventoImportar'],
    listarsvp: ['/eventoImportar'],
};

const bloqueadaPara = (perfil, pathname) => {
    const blockedRoutes = blockedRoutesByPerfil[perfil];
    return !!blockedRoutes && blockedRoutes.some((blocked) => pathname.startsWith(blocked));
};

const liberadaPara = (perfil, pathname) => {
    const allowedRoutes = restrictedRoutesByPerfil[perfil];

    if (!allowedRoutes) {
        return true;
    }

    return allowedRoutes.some((allowedRoute) => (
        allowedRoute.endsWith('*')
            ? pathname.startsWith(allowedRoute.slice(0, -1))
            : (pathname === allowedRoute || pathname.startsWith(allowedRoute + '/'))
    ));
};

// A rota abre para o perfil? A mesma regra do PrivateRoute, sem o `roles` de cada rota: serve para a
// tela não mostrar atalho para uma rota que devolveria o usuário para o painel.
export const podeAbrirRota = (perfil, pathname) => !bloqueadaPara(perfil, pathname) && liberadaPara(perfil, pathname);

export default function PrivateRoute({ component: Component, roles, ...rest }) {
    const hasRouteAccess = (perfil, pathname) => {
        if (bloqueadaPara(perfil, pathname)) {
            return false;
        }

        // Allowlist explícita da rota: quando `roles` é informado, só os perfis listados acessam.
        if (Array.isArray(roles) && roles.length > 0) {
            return roles.includes(perfil);
        }

        return liberadaPara(perfil, pathname);
    };

    return (
        <Route
            {...rest}
            render={props => {
                const auth = getAuthForPath(props.location.pathname);
                const perfil = auth.perfil;
                const doCargo = auth.token ? telaDoCargo(perfil, props.location) : null;
                if (doCargo) {
                    return <Redirect to={{ pathname: doCargo }} />;
                }

                return auth.token ? (
                    hasRouteAccess(perfil, props.location.pathname) ? (
                        <Component {...props} />
                    ) : (
                        <Redirect
                            to={{
                                pathname: fallbackRouteByPerfil[perfil] || '/principal',
                            }}
                        />
                    )
                ) : (
                        <Redirect
                            to={{
                                pathname: '/login',
                                state: { from: props.location }
                            }}
                        />
                    );
            }}
        />
    );
}
